import { Reaction, ReactionsMessage } from "@webng-types/changebridge";
import { useCallback, useEffect, useRef } from "react";
import { useChangeBridgeContext } from "../change-stream/useChangeBridge";
import { useState } from "react";
import { useApi } from "@webng/react-app-common";
import { useQuery } from "@tanstack/react-query";
import { ApiClient } from "@webng/tickaroo-api";

interface WebSocketMessage {
  type: 'r_reactions' | 'r_subscribeResponse';
  message?: string;
  eventId?: string;
  reactionId?: string;
  count?: number;
  status?: string;
  data?: {
    counts: {
      [eventId: string]: {
        [reactionId: string]: number;
      };
    };
    timestamp: number;
  };
}

export function useReactions(eventIds: string[], reactionsEnabled: boolean) {
  const changeBridgeValue = useChangeBridgeContext();

  if(!changeBridgeValue) {
    throw new Error("ChangeBridgeContext not found");
  }

  const { ws, hasErrored, isConnected, addEventListener, removeEventListener } = changeBridgeValue;

  const [reactionCounts, setReactionCounts] = useState<{ [eventId: string]: { [reactionId: string]: number } } | undefined>(undefined);
  const [loadedEvents, setLoadedEvents] = useState<Set<string>>(new Set());
  const pendingSubscriptionsRef = useRef<Set<string>>(new Set());

  const onOpen = useCallback((ws: WebSocket) => {
    if (!ws) {
      return;
    }

    if (pendingSubscriptionsRef.current.size > 0) {
      const eventIds = Array.from(pendingSubscriptionsRef.current);
      ws.send(JSON.stringify({
        type: 'r_subscribe',
        eventIds
      }));
      pendingSubscriptionsRef.current.clear();
    }
  }, []);

  const onMessage = useCallback((data: WebSocketMessage) => {
    if(!data.type.startsWith("r_")) {
      return;
    }

    if (data.type === "r_subscribeResponse" && data.status === "success" && data.data?.counts) {
      setReactionCounts(prev => ({
        ...prev,
        ...data.data!.counts
      }));
      return;
    }

    if (data.type === "r_reactions") {
      const reactionsData = data as ReactionsMessage;

      setReactionCounts(prev => {
        const counts: { [eventId: string]: { [reactionId: string]: number } } = { ...prev }
        reactionsData.reactions.forEach(r => {
          counts[r.eventId] = {
            ...counts[r.eventId],
            [r.reactionId]: r.count
          }
        })
        return counts;
      })
      return;
    }

  }, [setReactionCounts]);

  const subscribe = useCallback((eventIds: string[]) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      eventIds.forEach(id => pendingSubscriptionsRef.current.add(id));
      return;
    }

    try {
      ws.send(JSON.stringify({
        type: 'r_subscribe',
        eventIds
      }));
    } catch (error) {
      console.error("Error sending subscribe message:", error);
    }
  }, [ws]);

  const unsubscribe = useCallback((eventIds: string[]) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      eventIds.forEach(id => pendingSubscriptionsRef.current.delete(id));
      return;
    }

    try {
      ws.send(JSON.stringify({
        type: 'r_unsubscribe',
        eventIds
      }));
    } catch (error) {
      console.error("Error sending unsubscribe message:", error);
    }
  }, [ws]);

  useEffect(() => {
    if (!reactionsEnabled) {
      return;
    }

    if (eventIds.every(id => loadedEvents.has(id))) {
      return;
    }

    const addedEvents = eventIds.filter(id => !loadedEvents.has(id));
    const removedEvents = Array.from(loadedEvents).filter(id => !eventIds.includes(id));

    if(addedEvents.length > 0) {
      subscribe(addedEvents);
    }

    if(removedEvents.length > 0) {
      unsubscribe(removedEvents);
    }

    setLoadedEvents(new Set(eventIds));
  }, [reactionsEnabled, eventIds, loadedEvents, subscribe, unsubscribe, setLoadedEvents]);

  const onClose = useCallback(() => {
    pendingSubscriptionsRef.current.clear();
    setLoadedEvents(new Set());
  }, [pendingSubscriptionsRef, setLoadedEvents]);

  useEffect(() => {
    if (!reactionsEnabled) {
      return;
    }

    addEventListener("open", onOpen);
    addEventListener("message", onMessage);
    addEventListener("close", onClose);
    return () => {
      removeEventListener("open", onOpen);
      removeEventListener("message", onMessage);
      removeEventListener("close", onClose);
    }
  }, [reactionsEnabled, onOpen, onMessage, onClose, addEventListener, removeEventListener]);

  // Sometimes, onOpen is not called, I don't know why, seems to be a complicated timing issue
  // this makes sure that - if we have a standing connection and pending subscriptions - we subscribe
  // even though onOpen should have done that already
  useEffect(() => {
    if(ws && ws.readyState === WebSocket.OPEN && reactionCounts === undefined && pendingSubscriptionsRef.current.size > 0) {
      subscribe(Array.from(pendingSubscriptionsRef.current));
      pendingSubscriptionsRef.current.clear();
    }
  }, [ws, reactionCounts, pendingSubscriptionsRef.current.size, subscribe]);

  return {
    reactionCounts,
    isConnected,
    hasErrored
  }

}

export function useUserReactions(liveblogLocalId: string | undefined, userId: string | undefined, organizationId: string | undefined) {
  const api = useApi();
  return useQuery({
      queryKey: ["userReactions", liveblogLocalId, userId, organizationId],
      queryFn: async () => {
          if (!liveblogLocalId || !userId || !organizationId) return undefined;
          // Assumes the API returns all user reactions for all events in the liveblog
          // If the API is different, adjust accordingly

          const response: {reactions: Reaction[]} = await api.getJson(ApiClient.API_INTERACTIVE_V1_REACTIONS_GET, {
              organizationId: organizationId,
              liveblogId: liveblogLocalId,
              userId,
          });
          const reactions = response.reactions.filter(reaction => reaction.liveblogId === liveblogLocalId);

          // Convert array of reactions into a map of {eventId: {reactionId: true}}
          const eventReactionsMap = reactions.reduce((acc, reaction) => {
              const eventId = reaction.reactionKey.split("#")[1];
              const reactionId = reaction.reactionId;

              if (!acc[eventId]) {
                  acc[eventId] = {};
              }
              acc[eventId][reactionId] = true;

              return acc;
          }, {} as {[eventId: string]: {[reactionId: string]: boolean}});

          return eventReactionsMap;
      },
      enabled: !!liveblogLocalId && !!userId && !!organizationId,
  });
}
